import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DateInput from "@/components/DateInput";
import LeaveOverview from "@/pages/operations/LeaveOverview";
import TeamLeaveCalendar from "@/pages/operations/TeamLeaveCalendar";

const errText = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);

function LeaveRow({ r, onDone }) {
  const [reason, setReason] = useState(""); const [notify, setNotify] = useState(false); const [err, setErr] = useState(""); const [cert, setCert] = useState(false);
  const [kind, setKind] = useState("close");
  const LABEL = { approve: "Approve", decline: "Decline", cancel: "Cancel (restore hours)" };
  async function decide(action, override = false) {
    if (!override && !window.confirm(`${LABEL[action]} ${r.worker_name}'s ${r.type.toLowerCase()}?`)) return;
    try { await api.post(`/leave/${r.id}/decide`, { action, reason, notify_worker: notify, override, bereavement_kind: kind, medical_certificate: cert }); onDone(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "short") {
        if (window.confirm(`${d.message}\n\nApprove anyway? The balance will go negative.`)) decide(action, true);
        return;
      }
      setErr(errText(e, "Couldn't save"));
    }
  }
  return (
    <div className="border border-white/10 rounded p-3 space-y-2" data-testid={`leave-req-${r.id}`}>
      <div className="flex justify-between gap-2 flex-wrap">
        <div>
          <div className="font-medium">{r.worker_name} · {r.type}</div>
          <div className="text-sm opacity-80">{fmtDate(r.from_date)}{r.to_date !== r.from_date && ` – ${fmtDate(r.to_date)}`} · {r.working_days} working day{r.working_days === 1 ? "" : "s"} · {r.hours} h{r.part_day_hours ? " (part day)" : ""}</div>
          {r.note && <div className="text-xs opacity-70">Note: {r.note}</div>}
          {r.medical_certificate && <div className="text-xs text-green-400" data-testid={`leave-cert-${r.id}`}>Medical certificate provided</div>}
          {r.decline_reason && <div className="text-xs text-red-300">Reason: {r.decline_reason}</div>}
          {r.decided_by && <div className="text-[11px] opacity-50">{r.status} by {r.decided_by} · {fmtDate(r.decided_at)}</div>}
        </div>
        <span className="badge badge-grey h-fit" data-testid={`leave-req-status-${r.id}`}>{r.status}</span>
      </div>
      {r.status === "Pending" && (
        <div className="flex gap-2 flex-wrap items-center">
          <input className="input flex-1 min-w-[10rem]" placeholder="Decline reason (optional)" value={reason} onChange={e => setReason(e.target.value)} data-testid={`leave-reason-${r.id}`} />
          <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} data-testid={`leave-notify-${r.id}`} />Email the worker</label>
          {r.type === "Sick leave" && <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={cert} onChange={e => setCert(e.target.checked)} data-testid={`leave-cert-tick-${r.id}`} />Medical certificate provided</label>}
          {r.type === "Bereavement leave" && <select className="input text-xs" value={kind} onChange={e => setKind(e.target.value)} data-testid={`leave-bereave-kind-${r.id}`}><option value="close">Close family (3 days)</option><option value="other">Other (1 day)</option></select>}
          <button className="btn-silver text-sm" onClick={() => decide("approve")} data-testid={`leave-approve-${r.id}`}>Approve</button>
          <button className="btn-ghost text-sm text-red-400" onClick={() => decide("decline")} data-testid={`leave-decline-${r.id}`}>Decline</button>
        </div>
      )}
      {r.status === "Approved" && (
        <div className="flex gap-2 flex-wrap items-center">
          {r.balance_deducted && Object.keys(r.balance_deducted).length > 0 && <span className="text-xs opacity-60">Deducted {Object.values(r.balance_deducted).reduce((a, b) => a + Math.abs(b), 0).toFixed(2)} h{r.override ? " (approved anyway)" : ""}</span>}
          <input className="input flex-1 min-w-[10rem]" placeholder="Reason (optional)" value={reason} onChange={e => setReason(e.target.value)} data-testid={`leave-reason2-${r.id}`} />
          <button className="btn-ghost text-sm" onClick={() => decide("cancel")} data-testid={`leave-cancel-approved-${r.id}`}>Cancel (restore hours)</button>
          <button className="btn-ghost text-sm text-red-400" onClick={() => decide("decline")} data-testid={`leave-decline-approved-${r.id}`}>Decline</button>
        </div>
      )}
      {err && <div className="text-sm text-red-400">{err}</div>}
    </div>
  );
}

function LeaveSummary() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState(""); const [rows, setRows] = useState(null);
  const ok = from && to;
  const run = () => api.get("/leave/summary", { params: { start: from, end: to } }).then(r => setRows(r.data));
  async function csv() {
    const r = await api.get("/leave/summary", { params: { start: from, end: to, format: "csv" }, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `leave-summary-${from}-to-${to}.csv`; a.click();
  }
  return (
    <div className="card space-y-2" data-testid="leave-summary">
      <h2 className="text-silver">Leave summary (Admin)</h2>
      <div className="text-xs opacity-60">Approved leave by worker and type for the period — for keying into PaySauce. The Hub doesn't track balances.</div>
      <div className="flex gap-2 items-end flex-wrap">
        <label className="text-sm">From<DateInput className="input" value={from} onChange={e => setFrom(e.target.value)} data-testid="leave-sum-from" /></label>
        <label className="text-sm">To<DateInput className="input" value={to} onChange={e => setTo(e.target.value)} data-testid="leave-sum-to" /></label>
        <button className="btn-silver text-sm" disabled={!ok} onClick={run} data-testid="leave-sum-run">Show</button>
        <button className="btn-ghost text-sm" disabled={!ok} onClick={csv} data-testid="leave-sum-csv">Download CSV</button>
      </div>
      {rows && (
        <table className="table"><thead><tr><th>Worker</th><th>Type</th><th>Days</th><th>Hours</th><th>Dates</th></tr></thead>
          <tbody>{rows.map((a, i) => <tr key={i} data-testid={`leave-sum-row-${i}`}><td>{a.worker_name}</td><td>{a.type}</td><td>{a.days}</td><td>{a.hours}</td><td className="text-xs">{a.dates.join(", ")}</td></tr>)}
            {rows.length === 0 && <tr><td colSpan="5" className="opacity-50">No approved leave in this period.</td></tr>}</tbody></table>
      )}
    </div>
  );
}

export default function LeaveAdmin({ onChange }) {
  const { user } = useAuth();
  const [list, setList] = useState([]); const [status, setStatus] = useState("Pending");
  const load = () => { api.get("/leave", { params: { status } }).then(r => setList(r.data)); onChange?.(); };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3" data-testid="leave-admin">
      <TeamLeaveCalendar />
      <div className="card flex gap-2 items-center flex-wrap">
        <h2 className="text-silver mr-2">Leave requests</h2>
        {["Pending", "Approved", "Declined", "Cancelled", ""].map(s => (
          <button key={s || "all"} className={`px-3 py-1.5 text-sm rounded ${status === s ? "bg-silver text-black" : "border border-neutral-800"}`} onClick={() => setStatus(s)} data-testid={`leave-filter-${s || "all"}`}>{s || "All"}</button>
        ))}
      </div>
      <div className="card space-y-2">
        {list.length === 0 && <div className="text-sm opacity-50">No {status.toLowerCase()} leave requests.</div>}
        {list.map(r => <LeaveRow key={r.id} r={r} onDone={load} />)}
      </div>
      {user?.role === "Admin" && <LeaveOverview />}
      {user?.role === "Admin" && <LeaveSummary />}
    </div>
  );
}
